import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, ArrowRight, X, Sparkles, TrendingUp, BookmarkCheck } from 'lucide-react';
import { sanitizeRedirectPath } from '../../utils/securityUtils';

export interface AuthPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetPath?: string;
  featureName?: string;
}

export const AuthPromptModal: React.FC<AuthPromptModalProps> = ({
  isOpen,
  onClose,
  targetPath = '/',
  featureName = 'Advanced Features',
}) => {
  const navigate = useNavigate();
  const modalRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const handleSignIn = () => {
    onClose();
    navigate('/login', {
      state: {
        from: sanitizeRedirectPath(targetPath),
        message: `Please sign in to access ${featureName}.`,
      },
    });
  };

  return (
    <div
      className="ns-auth-modal__overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-prompt-title"
    >
      <div className="ns-auth-modal" ref={modalRef}>
        {/* Close Button */}
        <button
          type="button"
          className="ns-auth-modal__close-btn"
          onClick={onClose}
          aria-label="Close dialog"
        >
          <X size={18} />
        </button>

        {/* Header Eyebrow */}
        <div className="ns-auth-modal__eyebrow">
          <Lock size={13} className="ns-auth-modal__eyebrow-icon" />
          <span>SIGN IN REQUIRED</span>
        </div>

        {/* Title & Subtitle */}
        <h2 id="auth-prompt-title" className="ns-auth-modal__title">
          Access {featureName}
        </h2>
        <p className="ns-auth-modal__subtitle">
          The <strong>Market Dashboard</strong> is free to explore. To access institutional setups,
          advanced breadth models, stock histories, and scanners, please sign in or create a free account.
        </p>

        {/* Feature Highlights */}
        <ul className="ns-auth-modal__features">
          <li className="ns-auth-modal__feature-item">
            <span className="ns-auth-modal__feature-icon">
              <Sparkles size={15} />
            </span>
            <span>
              <strong>Institutional Setups &amp; Scanners:</strong> Breakouts, Volume Shockers, and Volume Streaks with delivery confirmation.
            </span>
          </li>
          <li className="ns-auth-modal__feature-item">
            <span className="ns-auth-modal__feature-icon">
              <TrendingUp size={15} />
            </span>
            <span>
              <strong>Deep Market Breadth &amp; Structure:</strong> Multi-EMA participation curves, 52W expansion ratios, and Advance/Decline trends.
            </span>
          </li>
          <li className="ns-auth-modal__feature-item">
            <span className="ns-auth-modal__feature-icon">
              <BookmarkCheck size={15} />
            </span>
            <span>
              <strong>Stock Explorer &amp; Custom Watchlists:</strong> Multi-timeframe candlestick charts, pivot distance tracking, and personal watchlists.
            </span>
          </li>
        </ul>

        {/* Action Buttons */}
        <div className="ns-auth-modal__actions">
          <button
            type="button"
            className="ns-btn ns-btn--primary ns-auth-modal__submit-btn"
            onClick={handleSignIn}
          >
            <span>Sign In to Continue</span>
            <ArrowRight size={15} />
          </button>
          <button
            type="button"
            className="ns-btn ns-btn--secondary ns-auth-modal__cancel-btn"
            onClick={onClose}
          >
            Stay on Market Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthPromptModal;
